import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isValid as isValidDate } from 'date-fns';
import { tellihealthApi, patientApi, DeviceConnectionEntry } from '../services/api';
import { formatReadingData, readingTypeLabel } from '../utils/formatReading';
import { generateRpmReadingsPdf } from '../utils/generateRpmReadingsPdf';
import { isoToMaskedDate } from '../utils/dateInputMask';

interface Reading {
  id: number;
  device_id: string;
  reading_type: string;
  time: string;
  tz_offset_minutes: number | null;
  data: Record<string, any>;
  received_at: string;
}

interface PatientDeviceReadingsProps {
  patientId: string;
  /** Clinic branding for the shared report (name/logo). Not patient PII. */
  clinicName?: string;
  logoPath?: string;
  /** Only used to pre-fill the Send dialog; never written into any document. */
  patientPhone?: string;
  /** Current clinician name for time-tracking action log. */
  currentUserName?: string;
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

// Local YYYY-MM-DD (avoids the UTC off-by-one that toISOString() causes in
// negative-offset timezones). Converts a picked calendar day to the ISO the API wants.
const toLocalYmd = (d: Date): string => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

/** A picked date -> ISO yyyy-MM-dd, or '' while the field is empty or half-typed. */
const pickedToIso = (d: Date | null): string => (d && isValidDate(d) ? toLocalYmd(d) : '');

const PatientDeviceReadings: React.FC<PatientDeviceReadingsProps> = ({
  patientId,
  clinicName,
  logoPath,
  patientPhone,
  currentUserName,
}) => {
  const [readings, setReadings] = useState<Reading[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // PHA-874: every calendar day (UTC) the patient has a reading on, ALL TIME. Comes
  // back with every readings response and is never narrowed by the date filter — the
  // monitoring cycles below are anchored on it so they can't move when the view does.
  const [readingDays, setReadingDays] = useState<string[]>([]);
  // PHA-1124: the 30-day cycle anchor, straight from the BFF — the device's
  // stored First Data Date (the vendor's own first-reading date) when it has one,
  // else the earliest reading this patient owns. It can PRECEDE every reading we
  // hold: AJC's SmartMeter fleet was transmitting long before 09/03/2026, when
  // the feed was pointed at Phanova. Anchoring on our earliest row would restart
  // their cycles mid-stream and disagree with the billing report.
  const [cycleAnchor, setCycleAnchor] = useState<string | null>(null);
  // PHA-874: the APPLIED range, ISO yyyy-MM-dd, '' meaning unbounded. The tab now opens
  // on the patient's full history ('' / ''); it used to default to the last 30 days,
  // which hid older readings and silently re-anchored the monitoring cycles.
  // These are the source of truth for the API call and for every export.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // The PENDING picker values. Typing/picking only moves these; Apply commits them.
  // Keeping the fetch off the keystroke path is what stops the inputs from thrashing
  // (the readings spinner used to unmount the whole filter row on every character).
  const [fromPending, setFromPending] = useState<Date | null>(null);
  const [toPending, setToPending] = useState<Date | null>(null);
  const [deviceHistory, setDeviceHistory] = useState<DeviceConnectionEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // PHA-871: Send-to-patient dialog + feedback
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendPhone, setSendPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  const loadReadings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tellihealthApi.getPatientReadings(patientId, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        // Matches the BFF's cap (PHA-874). Anything beyond it is reported to the user
        // rather than silently dropped — see the truncation notice below.
        limit: 5000,
      });
      setReadings(data.readings || []);
      setTotal(data.total || 0);
      setReadingDays(data.readingDays || []);
      setCycleAnchor(data.cycleAnchor || null);
    } catch {
      setReadings([]);
      setReadingDays([]);
      setCycleAnchor(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, fromDate, toDate]);

  useEffect(() => {
    loadReadings();
  }, [loadReadings]);

  const loadDeviceHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await tellihealthApi.getPatientDeviceHistory(patientId);
      setDeviceHistory(data.history || []);
    } catch {
      setDeviceHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadDeviceHistory();
  }, [loadDeviceHistory]);

  // PHA-547: data days = unique calendar dates with at least one reading.
  // Computed client-side from the already-fetched readings; respects the From/To filter.
  const dataDays = useMemo(
    () => new Set(readings.map((r) => r.time.slice(0, 10))).size,
    [readings],
  );

  // Parse YYYY-MM-DD as noon UTC — avoids midnight-UTC shifting to the previous day
  // in negative-offset timezones (e.g. UTC-5) when displayed with toLocaleDateString().
  const parseDateNoonUtc = (s: string) =>
    Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10), 12);

  // PHA-547: monitoring cycles — 30-day buckets anchored on the patient's earliest reading.
  // Mirrors the billing-report SQL anchor (queries.yaml `first_reading` CTE) so this view
  // can't disagree with billing for the cycles it shows.
  //
  // PHA-874: built from `readingDays` (all-time, straight from the BFF), NOT from the
  // loaded rows. Anchoring on the earliest LOADED reading meant a date filter — or the
  // row cap — re-dated every cycle: the prod report showed cycle 1 starting a month ago
  // instead of on the patient's first reading. A cycle is a property of the patient, so
  // nothing the user does to the view may move it.
  const cycles = useMemo(() => {
    // Fall back to the loaded rows only if the BFF sent no day list (older server).
    const dates = readingDays.length > 0 ? readingDays : readings.map((r) => r.time.slice(0, 10));
    if (dates.length === 0) return [];
    const dayMs = 86400000;
    // PHA-1124: prefer the server's anchor (First Data Date, else earliest owned
    // reading). Falls back to the earliest loaded day only for an older server
    // that sends no cycleAnchor. The anchor is deliberately NOT clamped to the
    // first day we hold data for — see the state declaration above.
    const earliestDay = dates.reduce((a, b) => (a < b ? a : b));
    const anchorMs = parseDateNoonUtc(cycleAnchor || earliestDay);
    const buckets = new Map<number, Set<string>>();
    for (const d of dates) {
      const n = Math.floor((parseDateNoonUtc(d) - anchorMs) / (30 * dayMs));
      if (!buckets.has(n)) buckets.set(n, new Set());
      buckets.get(n)!.add(d);
    }
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayMs = parseDateNoonUtc(todayStr);
    const maxN = Math.floor((todayMs - anchorMs) / (30 * dayMs));
    const out: { n: number; start: Date; end: Date; dataDays: number; current: boolean }[] = [];
    for (let n = 0; n <= maxN; n++) {
      const start = new Date(anchorMs + n * 30 * dayMs);
      const end = new Date(anchorMs + ((n + 1) * 30 - 1) * dayMs);
      out.push({
        n,
        start,
        end,
        dataDays: buckets.get(n)?.size ?? 0,
        current: todayMs >= start.getTime() && todayMs <= end.getTime(),
      });
    }
    // PHA-1124: a transferred device's anchor can predate everything Phanova
    // holds, so the LEADING cycles are legitimately empty — for a fleet that
    // moved after a year that is a wall of zero-day rows above the real ones.
    // Drop the empty run at the front only (never an empty cycle between two
    // populated ones, and never the current cycle, whose emptiness is the point).
    const firstUseful = out.findIndex((c) => c.dataDays > 0 || c.current);
    const trimmed = firstUseful > 0 ? out.slice(firstUseful) : out;
    return trimmed.reverse();
  }, [readingDays, readings, cycleAnchor]);

  const bpReadings = readings
    .filter((r) => r.reading_type === 'BloodPressure')
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const chartData = bpReadings.map((r) => ({
    time: new Date(r.time).toLocaleString(),
    systolic: r.data?.systolic,
    diastolic: r.data?.diastolic,
    pulse: r.data?.pulse,
  }));

  const handleExportCSV = () => {
    if (readings.length === 0) return;
    const headers = ['Date/Time', 'Reading Type', 'Device ID', 'Data'];
    const rows = readings.map((r) => [
      new Date(r.time).toISOString(),
      r.reading_type,
      r.device_id,
      formatReadingData(r.reading_type, r.data),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device-readings-${patientId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // PHA-871: Export the current (filtered) readings to a patient-friendly PDF.
  // Requires an explicit From/To range. Contains no PII — only readings + dates + clinic branding.
  const handleExportPdf = async () => {
    if (!reportFrom || !reportTo || readings.length === 0) return;
    try {
      const blob = await generateRpmReadingsPdf(readings, {
        clinicName,
        startDate: reportFrom,
        endDate: reportTo,
        logoPath,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rpm-readings-${reportFrom}_to_${reportTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, severity: 'error', message: 'Failed to generate PDF' });
    }
  };

  // PHA-871: Send the readings report to the patient by SMS. Generates the same PDF,
  // persists it under a token, and delegates the SMS to the backend (org template).
  const handleOpenSendDialog = () => {
    setSendPhone(patientPhone || '');
    setSendError(null);
    setSendDialogOpen(true);
  };

  const handleCloseSendDialog = () => {
    setSendDialogOpen(false);
    setSendPhone('');
    setSendError(null);
  };

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        // strip the "data:application/pdf;base64," prefix
        resolve(result.slice(result.indexOf(',') + 1));
      };
      reader.onerror = () => reject(new Error('Failed to read PDF blob'));
      reader.readAsDataURL(blob);
    });

  const handleSendReport = async () => {
    if (!reportFrom || !reportTo || !sendPhone || readings.length === 0) return;
    setSending(true);
    setSendError(null);
    const actionStartTime = new Date();
    try {
      const blob = await generateRpmReadingsPdf(readings, {
        clinicName,
        startDate: reportFrom,
        endDate: reportTo,
        logoPath,
      });
      const pdfBase64 = await blobToBase64(blob);
      // Strip to the minimal non-PII shape before it leaves the app: the public
      // report page must never receive device_id or any identifier.
      const snapshot = readings.map((r) => ({
        time: r.time,
        reading_type: r.reading_type,
        data: r.data,
      }));
      await tellihealthApi.sendRpmReport(patientId, {
        phoneNumber: sendPhone,
        startDate: reportFrom,
        endDate: reportTo,
        pdfBase64,
        readings: snapshot,
      });
      try {
        await patientApi.logAction({
          action: 'RPM Readings Sent',
          action_begin_ts: actionStartTime.toISOString(),
          action_end_ts: new Date().toISOString(),
          action_user: currentUserName || 'Unknown User',
          patient_id: patientId,
          details: { phone_number: sendPhone, start_date: reportFrom, end_date: reportTo },
        });
      } catch {
        /* time-tracking log is best-effort */
      }
      handleCloseSendDialog();
      setSnackbar({ open: true, severity: 'success', message: 'Readings sent to patient!' });
    } catch (err: any) {
      setSendError(err?.message || 'Failed to send readings');
    } finally {
      setSending(false);
    }
  };

  // PHA-871's exports label the report with a range, so they used to need an explicit
  // From AND To. PHA-874: with the tab defaulting to the whole history, that would have
  // left Export PDF and Send to Patient dead on arrival. When a bound is unset the
  // report falls back to the span of what is actually on screen, so a full-history PDF
  // is reachable and still states the dates it covers.
  const reportFrom = fromDate || (readings.length > 0
    ? readings.map((r) => r.time.slice(0, 10)).reduce((a, b) => (a < b ? a : b))
    : '');
  const reportTo = toDate || (readings.length > 0
    ? readings.map((r) => r.time.slice(0, 10)).reduce((a, b) => (a > b ? a : b))
    : '');
  const rangeReady = Boolean(reportFrom && reportTo && readings.length > 0);

  // Apply is only offered when the pickers actually differ from what is on screen,
  // so the button doubles as the "you have unapplied dates" indicator.
  const pendingFromIso = pickedToIso(fromPending);
  const pendingToIso = pickedToIso(toPending);
  const rangeDirty = pendingFromIso !== fromDate || pendingToIso !== toDate;
  const rangeInverted = Boolean(pendingFromIso && pendingToIso && pendingFromIso > pendingToIso);

  const applyRange = () => {
    if (rangeInverted) return;
    setFromDate(pendingFromIso);
    setToDate(pendingToIso);
  };

  const showAllDates = () => {
    setFromPending(null);
    setToPending(null);
    setFromDate('');
    setToDate('');
  };

  // The row cap is generous, but say so rather than quietly truncating history.
  const truncated = total > readings.length;

  return (
    <Box sx={{ mt: 2 }}>
      {/* Device Connection History — PHA-542 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Device Connection History
        </Typography>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : deviceHistory.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No device history for this patient.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Device Connection History">
              <TableHead>
                <TableRow>
                  <TableCell>IMEI</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Linked</TableCell>
                  <TableCell>Removed</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deviceHistory.map((entry) => (
                  <TableRow key={`${entry.device_id}-${entry.added_at}`}>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {entry.device_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{entry.serial_number ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{entry.device_type ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{entry.manufacturer ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(entry.added_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(entry.remove_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      {entry.remove_at === null ? (
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                          variant="outlined"
                          aria-label="Device status: Active"
                        />
                      ) : (
                        <Chip
                          label="Removed"
                          size="small"
                          color="default"
                          variant="outlined"
                          aria-label="Device status: Removed"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Filters + actions — kept on a single row; the action buttons stay grouped
          (nowrap) so they never split across lines. Stats live on their own line below.
          PHA-874: this row sits OUTSIDE the loading branch on purpose. It used to live
          inside it, so every refetch unmounted the date fields mid-edit — that is what
          made them "jump after a single keystroke" and lose focus. */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* PHA-874: calendar pickers, like the billing report. PHA-876 still holds —
              `format` pins MM/DD/YYYY, so unlike a native <input type="date"> the
              display never follows the browser's locale into DD/MM/YYYY. Picking or
              typing only moves the pending value; the fetch waits for Apply. */}
          <DatePicker
            label="From"
            value={fromPending}
            onChange={(d) => setFromPending(d)}
            format="MM/dd/yyyy"
            maxDate={toPending && isValidDate(toPending) ? toPending : undefined}
            slotProps={{
              textField: { size: 'small', placeholder: 'MM/DD/YYYY', sx: { width: 170 } },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="To"
            value={toPending}
            onChange={(d) => setToPending(d)}
            format="MM/dd/yyyy"
            minDate={fromPending && isValidDate(fromPending) ? fromPending : undefined}
            slotProps={{
              textField: { size: 'small', placeholder: 'MM/DD/YYYY', sx: { width: 170 } },
              field: { clearable: true },
            }}
          />
          <Button
            variant={rangeDirty ? 'contained' : 'outlined'}
            size="small"
            onClick={applyRange}
            disabled={!rangeDirty || rangeInverted}
            title={rangeInverted ? 'The From date is after the To date' : undefined}
          >
            Apply
          </Button>
          <Button
            size="small"
            onClick={showAllDates}
            disabled={!fromDate && !toDate && !fromPending && !toPending}
          >
            Show all
          </Button>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={loading || readings.length === 0}
            >
              Export CSV
            </Button>
            {/* PHA-871: Export PDF + Send to Patient. Require an explicit From/To range. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPdf}
              disabled={loading || !rangeReady}
              title={!rangeReady ? 'No readings to export' : undefined}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              onClick={handleOpenSendDialog}
              disabled={loading || !rangeReady}
              title={!rangeReady ? 'No readings to send' : undefined}
            >
              Send to Patient
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>

      {/* Readings section: shows spinner during readings load, then chart/table/cycles */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: truncated ? 0.5 : 2 }}>
            Total readings: {total}  ·  Data days: {dataDays}
            {!fromDate && !toDate ? '  ·  All readings' : ''}
          </Typography>
          {truncated && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing the {readings.length} most recent of {total} readings — narrow the date
              range to see older ones.
            </Typography>
          )}

          {/* Blood Pressure Chart */}
          {chartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Blood Pressure</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="systolic" stroke="#ef4444" name="Systolic" dot={false} />
                  <Line type="monotone" dataKey="diastolic" stroke="#3b82f6" name="Diastolic" dot={false} />
                  <Line type="monotone" dataKey="pulse" stroke="#22c55e" name="Pulse" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Readings Table */}
          {readings.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Device</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {readings.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(r.time).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{readingTypeLabel(r.reading_type)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {formatReadingData(r.reading_type, r.data)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {r.device_id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No device readings found</Typography>
          )}

          {/* PHA-547: Monitoring cycles — 30-day buckets from earliest reading, current cycle first. */}
          {cycles.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Monitoring Cycles
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Monitoring Cycles">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cycle #</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell align="right">Data Days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cycles.map((c) => (
                      <TableRow
                        key={c.n}
                        sx={c.current ? { bgcolor: 'action.hover' } : undefined}
                      >
                        <TableCell>
                          {c.n + 1}
                          {c.current && (
                            <Chip
                              label="Current"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{c.start.toLocaleDateString()}</TableCell>
                        <TableCell>{c.end.toLocaleDateString()}</TableCell>
                        <TableCell align="right">{c.dataDays}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* PHA-871: Send readings to patient (mirrors the Send Survey Link dialog) */}
      <Dialog open={sendDialogOpen} onClose={handleCloseSendDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Readings to Patient</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              An SMS with a link to a Remote Patient Monitoring report ({isoToMaskedDate(reportFrom)} to {isoToMaskedDate(reportTo)}) will be
              sent to:
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              value={sendPhone}
              onChange={(e) => setSendPhone(e.target.value)}
              placeholder="5513145855"
              helperText="Enter phone number without spaces or dashes"
              autoFocus
            />
            {sendError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {sendError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSendDialog} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendReport}
            variant="contained"
            color="primary"
            disabled={sending || !sendPhone}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientDeviceReadings;
